import { Image } from 'expo-image';
import React, { useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { resolveBloomUniversalImageUrl } from '../utils/resolveImageUrl';

export interface BloomCmsIconProps {
  /** Contentful media URL — when missing/invalid, `fallback` is shown. */
  readonly url?: string | undefined;
  readonly size: number;
  readonly fallback: React.ReactNode;
  readonly accessibilityLabel?: string;
}

/**
 * Renders a Contentful icon asset, or the app SVG/node fallback.
 */
export function BloomCmsIcon({
  url,
  size,
  fallback,
  accessibilityLabel,
}: Readonly<BloomCmsIconProps>) {
  const resolvedUrl = useMemo(
    () => resolveBloomUniversalImageUrl(url),
    [url],
  );

  if (!resolvedUrl) {
    return <>{fallback}</>;
  }

  return (
    <View
      style={[styles.box, { width: size, height: size }]}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole={accessibilityLabel ? 'image' : undefined}
    >
      <Image
        source={{ uri: resolvedUrl }}
        style={styles.image}
        contentFit="contain"
        accessibilityIgnoresInvertColors
      />
    </View>
  );
}

const styles = StyleSheet.create({
  box: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    height: '100%',
  },
});

export default BloomCmsIcon;
